/**
 * Advisors feature — page-facing layouts, sections, and access helpers.
 */
export {
  ADVISORS_BASE,
  ADVISOR_WIL_TOOL_SLUG,
  ADVISORS_ASK_PATH,
  ADVISORS_SEATS_BASE,
  ADVISORS_OPPORTUNITIES_PATH,
  ADVISORS_MY_ADVISORS_ALL_PATH,
  ADVISORS_MY_FOUNDERS_ALL_PATH,
  ADVISORS_VIEW_ALL_LIST_LIMIT,
  ADVISORS_MY_FOUNDERS_PAGE_SIZE,
  ADVISORS_ORDER_BY_LAST_ACTIVITY,
  ADVISORS_UPGRADE_PATH,
  ADVISORS_SESSIONS_PATH,
  ADVISORS_PAYOUTS_PATH,
  ADVISORS_BROWSE_PATH,
  ADVISORS_DIRECTORY_PATH,
  ADVISORS_PROFILE_PATH,
  WIL_DISPLAY_NAME,
  WIL_AVATAR,
} from '@src/components/sco/Advisors/constants'
export type {
  AgreementRow,
  BaseballCardData,
  DirCategory,
  SeatCategory,
  SeatData,
} from '@src/components/sco/Advisors/types'

export { AdvisorProfileCard } from '@src/components/sco/Advisors/cards/AdvisorProfileCard'
export { InviteToBoardModal } from '@src/components/sco/Advisors/modals/InviteToBoardModal'
export { SessionRatingModal } from '@src/components/sco/Advisors/modals/SessionRatingModal'
export { WilWhoIsModal } from '@src/components/sco/Advisors/modals/WilWhoIsModal'
export { AdvisorsLayout, AdvisorsLandingLayout } from '@src/components/sco/Advisors/layout/AdvisorsLayout'
export { BecomeAdvisorLandingLayout } from '@src/components/sco/Advisors/layout/BecomeAdvisorLandingLayout'
export { ClarityAdvisorImportLayout } from '@src/components/sco/Advisors/layout/ClarityAdvisorImportLayout'
export { AdvisorsEmbeddedShell } from '@src/components/sco/Advisors/layout/AdvisorsEmbeddedShell'
export {
  useAdvisorsPaths,
  resolveAdvisorsBase,
  buildAdvisorsPaths,
  requestManageAdvisorsBase,
} from '@src/components/sco/Advisors/paths'
export { AdvisorsMainPanel } from '@src/components/sco/Advisors/layout/AdvisorsMainPanel'
export { AdvisorsNav } from '@src/components/sco/Advisors/layout/AdvisorsNav'
export { AdvisorChatAside } from '@src/components/sco/Advisors/layout/AdvisorChatAside'
export { EngagementAsidePanel } from '@src/components/sco/Advisors/layout/EngagementAsidePanel'
export { ManageEngagementPanel } from '@src/components/sco/Advisors/layout/ManageEngagementPanel'

export {
  FREE_WIL_MESSAGE_LIMIT,
  WIL_AI_UPGRADE_PRODUCT_ID,
  ADVISOR_TEAM_UPGRADE_PRODUCT_ID,
  LOCKED_TEAM_ADVISORS,
  TEAM_BOOK_MEETING_URL,
  TEAM_ADVISOR_BOOK_MEETING_URLS,
  teamAdvisorBadgeForName,
  findLockedTeamAdvisor,
  bookMeetingHrefForAdvisor,
  bookMeetingLabelForAdvisor,
  advisorFirstName,
  useAdvisorsAccess,
  remainingWilMessages,
  isWilMessageLimitReached,
} from '@src/components/sco/Advisors/model/access'
export type { LockedTeamAdvisor, TeamAdvisorBadge } from '@src/components/sco/Advisors/model/access'
export { FounderAdminInsights } from '@src/components/sco/Advisors/layout/FounderAdminInsights'
export { LockedTeamAdvisorMiniProfile } from '@src/components/sco/Advisors/layout/LockedTeamAdvisorMiniProfile'
export { WilMiniProfile } from '@src/components/sco/Advisors/layout/WilMiniProfile'
export { useWilMessageUsage } from '@src/components/sco/Advisors/hooks/useWilMessageUsage'

export { HomeSection } from '@src/components/sco/Advisors/sections/HomeSection'
export { AskSection } from '@src/components/sco/Advisors/sections/AskSection'
export { MyFoundersSection } from '@src/components/sco/Advisors/sections/MyFoundersSection'
export { MyFoundersListSection } from '@src/components/sco/Advisors/sections/MyFoundersListSection'
export { MyAdvisorsSection } from '@src/components/sco/Advisors/sections/MyAdvisorsSection'
export { DirectorySection } from '@src/components/sco/Advisors/sections/DirectorySection'
export { BrowseSection } from '@src/components/sco/Advisors/sections/BrowseSection'
export { AdvisorProfileSection } from '@src/components/sco/Advisors/sections/AdvisorProfileSection'
export { BoardSeatsSection } from '@src/components/sco/Advisors/sections/BoardSeatsSection'
export { CreateSeatSection } from '@src/components/sco/Advisors/sections/CreateSeatSection'
export { CompCalculatorSection } from '@src/components/sco/Advisors/sections/CompCalculatorSection'
export { AgreementSection } from '@src/components/sco/Advisors/sections/AgreementSection'
export { AgreementsSection } from '@src/components/sco/Advisors/sections/AgreementsSection'
export { SignSection } from '@src/components/sco/Advisors/sections/SignSection'
export { AdvisorApplicationSection } from '@src/components/sco/Advisors/sections/advisorApplication/AdvisorApplicationSection'
export { AdvisorProfileSetupSection } from '@src/components/sco/Advisors/sections/advisorProfile/AdvisorProfileSetupSection'
export { AdvisorProfileViewSection } from '@src/components/sco/Advisors/sections/advisorProfile/AdvisorProfileViewSection'
export { AdvisorProfileEditSection } from '@src/components/sco/Advisors/sections/advisorProfile/AdvisorProfileEditSection'
export { AdvisorProfileSettingsSection } from '@src/components/sco/Advisors/sections/advisorProfile/AdvisorProfileSettingsSection'
export { AdvisorProfilePayoutsSection } from '@src/components/sco/Advisors/sections/advisorProfile/AdvisorProfilePayoutsSection'
export { UpgradeSection } from '@src/components/sco/Advisors/sections/UpgradeSection'
export { AdvisorsLandingSection } from '@src/components/sco/Advisors/sections/AdvisorsLandingSection'
export { BecomeAdvisorLandingSection } from '@src/components/sco/Advisors/sections/BecomeAdvisorLandingSection'
export { ClarityAdvisorImportSection } from '@src/components/sco/Advisors/sections/ClarityAdvisorImportSection'
export { SeatDetailSection } from '@src/components/sco/Advisors/sections/SeatDetailSection'
export { OpportunitiesOverviewSection } from '@src/components/sco/Advisors/sections/opportunities/OpportunitiesOverviewSection'
export { OpportunitiesInvitesSection } from '@src/components/sco/Advisors/sections/opportunities/OpportunitiesInvitesSection'
export { OpportunitiesApplicationsSection } from '@src/components/sco/Advisors/sections/opportunities/OpportunitiesApplicationsSection'
export { OpportunitiesMatchesSection } from '@src/components/sco/Advisors/sections/opportunities/OpportunitiesMatchesSection'
export { OpportunitiesEngagementsSection } from '@src/components/sco/Advisors/sections/opportunities/OpportunitiesEngagementsSection'
export { InboxSection } from '@src/components/sco/Advisors/sections/InboxSection'
export { BoardSeatProfileSection } from '@src/components/sco/Advisors/sections/BoardSeatProfileSection'
export { SessionsListSection } from '@src/components/sco/Advisors/sections/SessionsListSection'
export { SessionDetailSection } from '@src/components/sco/Advisors/sections/SessionDetailSection'
export { SessionBookSection } from '@src/components/sco/Advisors/sections/SessionBookSection'
export { SessionRoomSection } from '@src/components/sco/Advisors/sections/SessionRoomSection'
export { PayoutsSection } from '@src/components/sco/Advisors/sections/PayoutsSection'
export { SessionSettingsSection } from '@src/components/sco/Advisors/sections/SessionSettingsSection'
export { SessionAvailabilitySection } from '@src/components/sco/Advisors/sections/SessionAvailabilitySection'
export { OpenSeatMatchCard } from '@src/components/sco/Advisors/cards/OpenSeatMatchCard'
export { WilMessagePaywall } from '@src/components/sco/Advisors/shared/WilMessagePaywall'
export { TeamAdvisorPaywall } from '@src/components/sco/Advisors/shared/TeamAdvisorPaywall'
export { PaywalledAdvisorAction } from '@src/components/sco/Advisors/shared/PaywalledAdvisorAction'
